import React, { useState, useEffect, useRef } from 'react';
import { contentItemsAPI } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Search, Layers, ArrowRight } from 'lucide-react';

export const ContentItemLinkDialog = ({ open, onOpenChange, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef();

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await contentItemsAPI.getAll({ search: query, limit: 20 });
        setResults(data);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden" data-testid="content-item-link-dialog">
        <DialogHeader>
          <DialogTitle>Link Content Dev Course</DialogTitle>
          <DialogDescription>Search Skilltrac's Content Development tracker to link this delivery to a course being built.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search content items by title..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-10"
              data-testid="content-item-link-search-input"
            />
          </div>
          <div className="max-h-80 overflow-y-auto overflow-x-hidden space-y-2 pr-1 -mr-1" data-testid="content-item-link-results">
            {searching ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
            ) : results.length > 0 ? (
              results.map(item => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item)}
                  className="w-full flex items-center justify-between gap-3 text-left border border-border rounded-lg p-3 hover:border-lime hover:bg-lime/5 transition-colors min-w-0"
                  data-testid={`content-item-link-result-${item.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      <Badge variant="outline" className="text-[10px]">{item.technology_area}</Badge>
                      <Badge variant="outline" className="text-[10px] bg-slate-50">{item.catalog_status}</Badge>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </button>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">
                {query ? 'No matching content items found.' : 'Start typing to search...'}
              </p>
            )}
          </div>
          <Button variant="outline" className="w-full" onClick={() => onSelect(null)} data-testid="content-item-link-skip-btn">
            <Layers className="w-4 h-4 mr-2" />Not linked to a Content Dev course
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContentItemLinkDialog;
